//* 화환 주문 CS 요약 파생값 — "그대로 렌더하면 상담원이 틀린 답을 하는" 필드들을 한 곳에서 번역한다.
//* 계약·근거: docs/backend-request-2026-08-14.md §1 (백엔드 회신)
//*
//* 여기 있는 함수들은 단순 포맷팅이 아니라 **운영 의미 판정**이다. 화면에서 직접
//* order.canceledAt / order.amount 를 찍지 말고 반드시 이걸 거칠 것.

import { ShopOrderCsMemoLogItem } from '@shared/api/shopOrderCsMemo/shopOrderCsMemo.interface';
import { PaymentState } from '@shared/types';
import { orderBrandName } from '@shared/utils/bugoBrandName.util';
import { CsMemoStatRow } from '@shared/utils/csMemoStats';
import { formatPrice } from '@shared/utils/formatPrice';

/** cancelProgress·refundRoute·amount 판정에 필요한 최소 형태 (로그 요약·주문 문서 양쪽 수용) */
export interface ShopOrderCsAmountSource {
  paymentStatus?: string;
  amount?: number;
  paymentByCash?: number;
  paymentByPoint?: number;
  /** 서버가 확정해 내리는 환불 경로 — 프론트가 method·isVbank로 재판정하지 않는다 */
  refundRoute?: 'Point' | 'Pg' | 'Manual';
  canceledAt?: Date;
  manualRefundStatus?: string;
  manualRefundProcessedAt?: Date;
}

export interface ShopOrderCsDateSource {
  deliveryStatus?: string;
  deliveredAt?: Date;
  estimatedDeliveredAt?: Date;
}

/**
 * 취소·환불 진행 표기.
 *
 * canceledAt은 취소가 **확정될 때가 아니라 요청될 때** 찍힌다(CancelRequested에도 들어옴).
 * 그래서 날짜만 뽑아 "취소 08.13"으로 쓰면 **환불이 아직 안 나간 건도 환불됐다고 안내**하게 된다.
 * 가상계좌·무통장은 PG 자동환불이 안 돼 admin이 직접 계좌이체하므로, 돈이 실제로 나간 시각은
 * manualRefundProcessedAt이고 canceledAt과 며칠 차이난다.
 *
 * 취소 이력이 없으면 null.
 */
export const cancelProgress = (
  order: ShopOrderCsAmountSource,
): { label: string; date: Date } | null => {
  const { paymentStatus } = order;

  if (paymentStatus === PaymentState.CANCEL_REQUESTED) {
    //* 아직 환불 전 — "취소됨"이라고 읽히면 안 된다
    return order.canceledAt ? { label: '취소요청', date: order.canceledAt } : null;
  }

  if (paymentStatus === PaymentState.CANCELED) {
    //* 수기환불(계좌이체)로 처리된 건은 실제 송금 시각을 보여준다
    if (order.manualRefundProcessedAt) {
      return { label: '환불완료', date: order.manualRefundProcessedAt };
    }
    return order.canceledAt ? { label: '취소', date: order.canceledAt } : null;
  }

  //* manualRefundStatus의 다른 값(Requested/Rejected)은 라벨을 바꾸지 않는다 —
  //* 결제 상태가 아직 안 넘어간 단계라 여기서 단정하면 오안내가 된다.
  return null;
};

/**
 * 금액 분해 표기 — 모달 전용.
 *
 * 표에는 합계(amount) 하나만 쓴다. 모달에서 분해하는 이유는 **환불 경로가 paymentByCash로 갈리기**
 * 때문이다: 합계 105,000원만 보고 "105,000원 환불됩니다"라고 답하면, 실제로는
 * "100,000원 카드 환불(2~3영업일) + 5,000P 즉시 반환"인 경우가 있다.
 * 운영 알림톡도 `총결제금액: 100,000원 5,000P`로 분리 표기하고 있어 화면만 합쳐 말하면 서로 어긋난다.
 */
export const amountBreakdown = (order: ShopOrderCsAmountSource): string | undefined => {
  const { amount, paymentByCash, paymentByPoint } = order;
  if (paymentByPoint && paymentByCash !== undefined) {
    return `${formatPrice(paymentByCash)}원 + ${formatPrice(paymentByPoint)}P`;
  }
  if (amount === undefined) return undefined;
  return `${formatPrice(amount)}원`;
};

/**
 * 환불 경로 안내 — 모달 전용. "환불 언제 되냐"에 답할 때 경로별로 소요가 다르다.
 *
 * 판정은 **서버가 확정해 내린 order.refundRoute 하나로만** 한다.
 * 프론트가 method·isVbank를 해석하던 이전 방식은 부트페이 레거시 건에서 틀린다 —
 * 부트페이는 클라이언트가 보낸 dto.method를 그대로 저장하고 vBank도 안 채우기 때문에,
 * 실제로는 수기 환불(계좌이체)해야 하는 건이 "카드 · PG 취소"로 안내됐다.
 * (백엔드 가이드 cs-memo-log-enrich-guide.md §2-1)
 */
export const refundRouteLabel = (order: ShopOrderCsAmountSource): string | undefined => {
  switch (order.refundRoute) {
    case 'Point':
      //* PG를 안 거친다 — 포인트 즉시 환급 + 취소 즉시 확정
      return '전액 포인트 · 즉시 환급';
    case 'Pg':
      //* PG 취소 요청 후 웹훅이 확정한다
      return 'PG 취소 · 확정까지 2~3영업일';
    case 'Manual':
      //* PG 자동취소 불가 — admin이 직접 계좌이체. 진행은 manualRefundStatus로 확인
      return '수기 환불(계좌이체) · 관리자 처리 필요';
    default:
      return undefined;
  }
};

/**
 * 배송일 한 칸 — 완료 건은 실제 완료일, 그 외는 예정일.
 * "이 주문 배송 언제?"라는 하나의 질문에 답하는 칸이라 컬럼을 나누지 않는다
 * (완료/예정 구분은 배송 상태 뱃지가 이미 해준다).
 */
export const deliveryDateInfo = (
  order: ShopOrderCsDateSource,
): { date: Date; estimated: boolean } | null => {
  if (order.deliveredAt) return { date: order.deliveredAt, estimated: false };
  if (order.estimatedDeliveredAt) {
    return { date: order.estimatedDeliveredAt, estimated: true };
  }
  return null;
};

/**
 * CS 응대 로그 통계 입력으로 접기 (답례품과 같은 집계기를 쓴다).
 * 화환은 결제·배송이 **다른 축**이라 두 상태를 따로 넘긴다 — 하나로 합치면
 * "결제취소인데 배송완료" 같은 실제로 파고들어야 할 조합이 통계에서 사라진다.
 */
export const toShopCsMemoStatRows = (
  items: ShopOrderCsMemoLogItem[],
  labels: {
    paymentStatus: (status: string) => string;
    deliveryStatus: (status: string) => string;
  },
): CsMemoStatRow[] =>
  items.map((item) => ({
    createdAt: item.createdAt,
    byName: item.byName,
    ...(item.adminOnly ? { adminOnly: true } : {}),
    body: item.body,
    axisKey: item.shopOrder,
    ...(item.order?.paymentStatus
      ? { paymentStatus: `결제 ${labels.paymentStatus(item.order.paymentStatus)}` }
      : {}),
    ...(item.order?.deliveryStatus
      ? { status: `배송 ${labels.deliveryStatus(item.order.deliveryStatus)}` }
      : {}),
    ...(item.order?.itemName ? { itemName: item.order.itemName } : {}),
    ...(orderBrandName(item.order) ? { brand: orderBrandName(item.order) } : {}),
  }));
